import { RefObject, useCallback, useEffect } from 'react';
import { HeroConfig } from '../branding/hero';
import { HeaderConfig } from '../branding/header';
import { FooterConfig, FooterOfficeInfo } from '../branding/footer';
import { ContentDatos } from '../contenido/templates';

export interface PreviewContenido {
  templateId: string;
  datos: ContentDatos | null;
  acento: string;
  animaciones: boolean;
}

interface PreviewConfigMessage {
  type: 'HERO_PREVIEW_CONFIG';
  templateCode: string;
  footerTemplateCode?: string;
  heroConfig: Partial<HeroConfig>;
  headerConfig: Partial<HeaderConfig>;
  footerConfig: Partial<FooterConfig>;
  officeInfo?: FooterOfficeInfo;
  contenido?: PreviewContenido;
}

interface PreviewMessagingApi {
  notifySearcherChange: (searcherId: string) => void;
  notifyModalOpen: () => void;
  notifyModalClose: () => void;
}

function parseAllowedOrigins(): string[] {
  return (process.env.RAZZLE_PREVIEW_ALLOWED_ORIGINS || '')
    .split(',')
    .map((origin) => origin.trim())
    .filter(Boolean);
}

function resolveTargetOrigin(allowedOrigins: string[]): string | null {
  if (typeof document !== 'undefined' && document.referrer) {
    try {
      const referrerOrigin = new URL(document.referrer).origin;
      if (allowedOrigins.includes(referrerOrigin)) return referrerOrigin;
    } catch {
      // ignore malformed referrer
    }
  }
  return allowedOrigins[0] ?? null;
}

// Embedded as an iframe (Personalizar's live preview) -> window.parent.
// Opened directly as a tab (Personalizar's "Previsualizar" button) -> window.opener.
// Neither -> standalone navigation, nothing to talk to.
function getMessageTarget(): Window | null {
  if (typeof window === 'undefined') return null;
  if (window.parent !== window) return window.parent;
  if (window.opener) return window.opener as Window;
  return null;
}

export function usePreviewMessaging(
  containerRef: RefObject<HTMLElement>,
  onConfig: (message: {
    templateCode: string;
    heroConfig: Partial<HeroConfig>;
    headerConfig: Partial<HeaderConfig>;
    footerConfig: Partial<FooterConfig>;
    footerTemplateCode?: string;
    officeInfo?: FooterOfficeInfo;
    contenido?: PreviewContenido;
  }) => void,
): PreviewMessagingApi {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const allowedOrigins = parseAllowedOrigins();
    const targetOrigin = resolveTargetOrigin(allowedOrigins);

    function handleMessage(event: MessageEvent): void {
      if (!allowedOrigins.includes(event.origin)) return;
      const data = event.data as PreviewConfigMessage | undefined;
      if (data?.type === 'HERO_PREVIEW_CONFIG') {
        onConfig({
          templateCode: data.templateCode,
          footerTemplateCode: data.footerTemplateCode,
          heroConfig: data.heroConfig,
          headerConfig: data.headerConfig,
          footerConfig: data.footerConfig,
          officeInfo: data.officeInfo,
          contenido: data.contenido,
        });
      }
    }

    window.addEventListener('message', handleMessage);

    const target = getMessageTarget();
    if (targetOrigin && target) {
      target.postMessage({ type: 'HERO_PREVIEW_READY' }, targetOrigin);
    }

    return () => window.removeEventListener('message', handleMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !containerRef.current) return undefined;

    const allowedOrigins = parseAllowedOrigins();
    const targetOrigin = resolveTargetOrigin(allowedOrigins);
    const target = getMessageTarget();
    if (!targetOrigin || !target) return undefined;

    const resizeObserver = new ResizeObserver((entries) => {
      const height = entries[0]?.contentRect.height;
      if (height) {
        target.postMessage({ type: 'HERO_PREVIEW_HEIGHT', height }, targetOrigin);
      }
    });
    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  });

  const notifySearcherChange = useCallback((searcherId: string) => {
    const target = getMessageTarget();
    if (!target) return;
    const targetOrigin = resolveTargetOrigin(parseAllowedOrigins());
    if (!targetOrigin) return;
    target.postMessage({ type: 'HERO_PREVIEW_SEARCHER_CHANGE', searcherId }, targetOrigin);
  }, []);

  const notifyModalOpen = useCallback(() => {
    const target = getMessageTarget();
    if (!target) return;
    const targetOrigin = resolveTargetOrigin(parseAllowedOrigins());
    if (!targetOrigin) return;
    target.postMessage({ type: 'HERO_PREVIEW_MODAL_OPEN' }, targetOrigin);
  }, []);

  const notifyModalClose = useCallback(() => {
    const target = getMessageTarget();
    if (!target) return;
    const targetOrigin = resolveTargetOrigin(parseAllowedOrigins());
    if (!targetOrigin) return;
    target.postMessage({ type: 'HERO_PREVIEW_MODAL_CLOSE' }, targetOrigin);
  }, []);

  return { notifySearcherChange, notifyModalOpen, notifyModalClose };
}
